import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Check, Sparkles, Loader2, ShieldCheck } from "lucide-react";

function formatBRL(value) {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
}

export default function Plans() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [plans, setPlans] = useState([]);
    const [billing, setBilling] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {});
        if (user) api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {});
    }, [user]);

    const canceled = new URLSearchParams(location.search).get("canceled");

    const subscribe = async (planId) => {
        setError("");
        if (!user) { navigate("/login", { state: { from: "/planos" } }); return; }
        setLoadingPlan(planId);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            setError(formatApiError(e));
            setLoadingPlan(null);
        }
    };

    const trialEnd = billing?.trial_ends_at ? new Date(billing.trial_ends_at) : null;
    const trialActive = trialEnd && trialEnd > new Date();
    const subEnd = billing?.subscription_current_period_end ? new Date(billing.subscription_current_period_end) : null;

    return (
        <div className="min-h-screen relative grain">
            <Header />
            <main className="max-w-[1400px] mx-auto px-6 md:px-10 pt-14 pb-24">
                <div className="overline text-[#5C5C5C]">Planos</div>
                <h1 className="font-display font-light text-5xl md:text-6xl mt-2 tracking-tight">
                    Acesso total ao <span className="font-editorial italic text-holo">Instituto</span>
                </h1>
                <p className="font-body text-[#5C5C5C] mt-4 max-w-2xl">
                    Mantenha acesso ilimitado às jornadas premium, biblioteca digital e novos lançamentos exclusivos.
                </p>

                {canceled && (
                    <div data-testid="checkout-canceled" className="mt-8 p-4 rounded-2xl bg-amber-50 border border-amber-200 font-body text-sm text-amber-800">
                        Pagamento cancelado. Você pode tentar novamente quando quiser.
                    </div>
                )}

                {billing?.subscription_active && subEnd && (
                    <div data-testid="sub-active" className="mt-8 p-5 rounded-2xl bg-gradient-to-r from-[#00D4D4]/15 to-[#9370DB]/10 border border-[#00D4D4]/30">
                        <div className="flex items-center gap-3">
                            <ShieldCheck className="w-6 h-6 text-[#00D4D4]" />
                            <div>
                                <div className="font-display text-lg">Assinatura ativa</div>
                                <div className="font-body text-sm text-[#5C5C5C]">
                                    Plano {billing.subscription_plan} · acesso até {subEnd.toLocaleDateString("pt-BR")}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {!billing?.subscription_active && trialActive && (
                    <div className="mt-8 p-5 rounded-2xl bg-white border border-black/5">
                        <div className="font-body text-sm text-[#5C5C5C]">
                            Você está no <span className="text-[#1A1A1A] font-display">período gratuito</span> até {trialEnd.toLocaleDateString("pt-BR")}. Assine para não perder o acesso.
                        </div>
                    </div>
                )}

                <div className="grid md:grid-cols-2 gap-6 mt-12">
                    {plans.map((p) => {
                        const isAnnual = p.id === "anual";
                        return (
                            <div
                                key={p.id}
                                data-testid={`plan-card-${p.id}`}
                                className={`relative p-9 rounded-3xl bg-white border transition-all ${
                                    isAnnual ? "border-[#D4AF37]/40 shadow-2xl shadow-[#D4AF37]/10" : "border-black/5"
                                }`}
                            >
                                {p.badge && (
                                    <span className="absolute -top-3 left-9 text-[10px] tracking-widest uppercase font-display px-3 py-1 rounded-full bg-[#1A1A1A] text-white">
                                        {p.badge}
                                    </span>
                                )}
                                <div className="overline text-[#5C5C5C]">Plano {p.name}</div>
                                <div className="flex items-baseline gap-2 mt-4">
                                    <div className="font-display font-light text-6xl tracking-tighter">{formatBRL(p.amount)}</div>
                                    <div className="font-body text-sm text-[#5C5C5C]">/ {p.interval_days === 365 ? "ano" : "mês"}</div>
                                </div>
                                <p className="font-body text-[#5C5C5C] mt-4">{p.description}</p>

                                <ul className="mt-8 space-y-3">
                                    {p.highlights.map((h, i) => (
                                        <li key={i} className="flex gap-3 items-start">
                                            <Check className="w-4 h-4 mt-1 text-[#00D4D4] shrink-0" />
                                            <span className="font-body text-sm text-[#1A1A1A]">{h}</span>
                                        </li>
                                    ))}
                                </ul>

                                <button
                                    data-testid={`plan-subscribe-${p.id}`}
                                    onClick={() => subscribe(p.id)}
                                    disabled={loadingPlan === p.id || billing?.subscription_active}
                                    className={`mt-10 w-full justify-center ${isAnnual ? "btn-holo" : "btn-primary"} ${loadingPlan === p.id ? "opacity-70" : ""}`}
                                >
                                    {loadingPlan === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                    {billing?.subscription_active ? "Assinatura ativa" : (loadingPlan === p.id ? "Redirecionando..." : `Assinar plano ${p.name}`)}
                                </button>
                            </div>
                        );
                    })}
                </div>

                {error && (
                    <div data-testid="plans-error" className="mt-6 text-sm text-red-600 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">{error}</div>
                )}

                <div className="mt-14 grid md:grid-cols-3 gap-6 text-sm font-body text-[#5C5C5C]">
                    <Trust title="Pagamento seguro" body="Processado pela Stripe — padrão de segurança bancário (PCI DSS Level 1)." />
                    <Trust title="Sem fidelidade" body="Cancele quando quiser. O acesso permanece até o fim do período pago." />
                    <Trust title="Acesso imediato" body="Liberação automática logo após a confirmação do pagamento." />
                </div>
            </main>
            <Footer />
        </div>
    );
}

function Trust({ title, body }) {
    return (
        <div className="p-6 rounded-2xl bg-white border border-black/5">
            <div className="font-display text-[#1A1A1A]">{title}</div>
            <div className="mt-2">{body}</div>
        </div>
    );
}
